// OrderDetailPage.tsx
import React, { useCallback, useEffect, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { Grid, Spin } from "antd";
import { STATUS_TAGS as STATUS_ORDER } from "../../../utils/constants";
import { PATHS } from "../../../app/router/paths";
import { orderApi } from "../../orders/api/orderApi";
import { ItemModel } from "../model/orders.ui";
import ConvertOrderDetail, { OrderDetail } from "../model/orderDetail.ui";
import { ConvertOrderUpdateRequest } from "../model/orderUpdate.ui";
import { OrderDetailDesktop } from "../components/OrderDetailDesktop";
import { OrderDetailMobile } from "../components/OrderDetailMobile";

const { useBreakpoint } = Grid;

interface OrderDetailPageProps {
  order?: ItemModel;
  isDesktop?: boolean;
  onBackToList?: () => void;
}

export const OrderDetailPage: React.FC<OrderDetailPageProps> = ({
  order: propOrder,
  isDesktop: propIsDesktop,
  onBackToList,
}) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { orderId } = useParams<{ orderId?: string }>();
  const screens = useBreakpoint();
  const [isLoading, setIsLoading] = useState<boolean>(true);

  const isDesktop = propIsDesktop ?? !!screens.md;
  const [orderDetail, setOrderDetail] = useState<OrderDetail>();

  const handleBack = (shouldReload: boolean | unknown = false) => {
    const reload = shouldReload === true;
    if (onBackToList) {
      onBackToList();
    } else {
      navigate(PATHS.ORDERS, { state: { reload } });
    }
  };

  const stateOrder = (location.state as { order?: ItemModel } | null)?.order;
  const targetId = propOrder?.id ?? stateOrder?.id ?? (orderId ? Number(orderId) : undefined);

  const fetchOrderDetail = useCallback(async () => {
    if (!targetId || Number.isNaN(targetId)) {
      setIsLoading(false);
      return;
    }
    setIsLoading(true);
    try {
      const response = await orderApi.getOrderById(targetId);
      if (response) {
        setOrderDetail(ConvertOrderDetail(response));
      }
    } catch (error) {
      console.error("Failed to fetch order detail:", error);
    } finally {
      setIsLoading(false);
    }
  }, [targetId]);

  useEffect(() => {
    fetchOrderDetail();
  }, [fetchOrderDetail]);

  const handleDownloadFile = () => {
    alert("ファイルのダウンロードに成功しました！"); // Tải file thành công!
  };

  const handleSave = async () => {
    if (targetId === null) return;
    const orderItems = orderDetail?.order_items?.filter((item) => item.is_selected);
    const updatedOrderDetail = {
      ...orderDetail,
      order_items: orderItems,
    };
    if (orderItems == null || orderItems.length === 0) {
      alert("商品を1つ以上選択してください！"); // Vui lòng chọn ít nhất một sản phẩm!
      return;
    }
    setIsLoading(true);
    const payload = ConvertOrderUpdateRequest(updatedOrderDetail);
    const isSuccess = await orderApi.updateOrder(targetId!, payload);
    if (isSuccess) {
      handleBack(true);
      alert("保存に成功しました！"); // Lưu thành công!
    }
  };

  const handleSendOrder = async () => {
    alert("注文を送信しました！"); // Gửi đơn hàng thành công!
  };

  const handleCheckboxChange = (orderItemId: number, isSelected: boolean) => {
    setOrderDetail((prevOrderDetail) => {
      if (!prevOrderDetail?.order_items) {
        return prevOrderDetail;
      }

      return {
        ...prevOrderDetail,
        order_items: prevOrderDetail.order_items.map((item) =>
          item.id === orderItemId ? { ...item, is_selected: isSelected } : item
        ),
      };
    });
  };

  const currentStep =
    orderDetail?.status === STATUS_ORDER.pending.code
      ? 0
      : orderDetail?.status === STATUS_ORDER.waiting_final_confirmation.code
        ? 1
        : orderDetail?.status === STATUS_ORDER.confirmed.code
          ? 2
          : 3;

  const totalAmount =
    orderDetail?.order_items?.reduce(
      (acc, item) => acc + Number(item.proposed_price ?? 1) * Number(item.order_quantity ?? 0),
      0
    ) || 0;

  return (
    <div style={{ position: "relative", minHeight: "100vh", width: "100%", backgroundColor: "#F8F9FA" }}>
      {isLoading && (
        <div style={{ position: "fixed", top: 0, left: 0, right: 0, bottom: 0, zIndex: 1000, display: "flex", alignItems: "center", justifyContent: "center", backgroundColor: "rgba(255, 255, 255, 0.5)" }}>
          <Spin size="large" tip="注文詳細を読み込み中..." /* Đang tải chi tiết đơn hàng... */ />
        </div>
      )}

      {isDesktop ? (
        <OrderDetailDesktop
          orderDetail={orderDetail}
          currentStep={currentStep}
          totalAmount={totalAmount}
          handleDownloadFile={handleDownloadFile}
          handleSave={handleSave}
          handleSendOrder={handleSendOrder}
        />
      ) : (
        <OrderDetailMobile
          orderDetail={orderDetail}
          currentStep={currentStep}
          totalAmount={totalAmount}
          handleBack={handleBack}
          handleCheckboxChange={handleCheckboxChange}
          handleDownloadFile={handleDownloadFile}
          handleSave={handleSave}
          handleSendOrder={handleSendOrder}
        />
      )}
    </div>
  );
};